import React from "react";

import {
  Checkbox,
  Typography,
  SvgCheapestQuote,
  SvgCheapestQuoteChecked,
  SvgFastestQuote,
  SvgFastestQuoteChecked,
  SvgForwardersPref,
  SvgForwardersPrefChecked,
  SvgDirectSailing,
  SvgDirectSailingChecked,
  SvgCapacityGuarantee,
  SvgCapacityGuaranteeChecked,
} from "@portex-pro/ui-components";
import clsx from "clsx";

interface QuotePreferenceCardProps {
  type: "cheapest" | "fastest" | "forwarder" | "direct-sailing" | "capacity-guarantee";
  title: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  position?: "top" | "middle" | "bottom" | "standalone";
}

const iconMap = {
  cheapest: { default: SvgCheapestQuote, checked: SvgCheapestQuoteChecked },
  fastest: { default: SvgFastestQuote, checked: SvgFastestQuoteChecked },
  forwarder: { default: SvgForwardersPref, checked: SvgForwardersPrefChecked },
  "direct-sailing": { default: SvgDirectSailing, checked: SvgDirectSailingChecked },
  "capacity-guarantee": { default: SvgCapacityGuarantee, checked: SvgCapacityGuaranteeChecked },
};

export const QuotePreferenceCard: React.FC<QuotePreferenceCardProps> = ({
  type,
  title,
  description,
  checked,
  onChange,
  position = "standalone",
}) => {
  const icons = iconMap[type];
  const IconComponent = checked ? icons.checked : icons.default;

  // Style based on position in grouped layout
  const getPositionStyles = () => {
    if (position === "standalone") {
      return "border-2 rounded-lg";
    }

    // For grouped cards, we don't need borders since parent handles outer border
    // Only add internal dividers between cards
    switch (position) {
      case "top":
        return "border-b border-gray-200";
      case "middle":
        return "border-b border-gray-200";
      case "bottom":
        return ""; // No border for bottom card
      default:
        return "border-2 rounded-lg";
    }
  };

  const getBackgroundStyles = () => {
    if (checked) {
      return "bg-blue-50";
    }
    if (position === "standalone") {
      return "bg-white hover:bg-gray-50";
    }
    return "bg-white hover:bg-gray-50";
  };

  return (
    <div
      className={clsx(
        "cursor-pointer transition-all w-full",
        getPositionStyles(),
        getBackgroundStyles(),
        // Add border color for standalone cards
        position === "standalone" && !checked && "border-gray-200 hover:border-gray-300",
        position === "standalone" && checked && "border-blue-500"
      )}
      onClick={() => onChange(!checked)}
    >
      <div className="flex items-center p-4 w-full">
        <Checkbox
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          onClick={(e) => e.stopPropagation()}
          color="primary"
          className="mr-3"
        />
        <div className="flex justify-between items-center w-full flex-1">
          <div>
            <Typography variant="subtitle1" className="font-medium">
              {title}
            </Typography>
            <Typography variant="body2" className="text-gray-600">
              {description}
            </Typography>
          </div>
          <div className="flex items-center ml-2">
            <IconComponent />
          </div>
        </div>
      </div>
    </div>
  );
};
